import { useEffect, useRef, useState } from 'react'
import { AgeGroup } from 'constants/components'
import {
  broadcastToParent,
  RegisteredEventName
} from '@finanzcheck/catalyst-pollard'
import { getCookie, COOKIE_KEY_FCID } from 'utils/cookie'

/*
  TODO: This hook is getting a bit long and complex. Consider refactoring
  it into a simpler structure, possibly splitting the timer and tab visibility logic into separate hooks or utility functions.
*/
type DacSkipButtonConfig = {
  isDacPage: boolean
  isDacShowOnCtaClick: boolean
  isPremium: boolean
  isPremiumOrOldAge: boolean
  isYoungOrMiddleAgeGroup: boolean
  ageGroup: AgeGroup
  dacWidgetState: string
  isDacSkipButtonTriggersIgnored: boolean // added for CAT-4463 test, for rollout use a concreate logic
  forcedDacOnTabActionTrigger: boolean
  /** Flags for active experiments affecting skip button behavior. */
  experimentFlags?: {
    /** When true (CAT-4478 V4), skip button is timer-only — CTA click must not trigger it immediately. */
    isInSkipDacV4Experiment?: boolean
  }
  /**
   * Text to show on the skip button when isPremiumOrOldAge is false
   * (e.g. for legacy flows that still render a skip button independently).
   */
  fallbackButtonText?: string
  /**
   * Custom timeout calculator. Receives { ageGroup, isPremium }, returns ms.
   * Defaults to: premium=15s, young=60s, others=20s.
   */
  getTimeout?: (params: { ageGroup: AgeGroup; isPremium: boolean }) => number
}

type DacSkipButtonResult = {
  showDacSkipButton: boolean
  skipButtonText: string
  skipButtonShownReason: string
}

const defaultGetTimeout = ({
  ageGroup,
  isPremium
}: {
  ageGroup: AgeGroup
  isPremium: boolean
}): number => {
  if (isPremium || ageGroup === AgeGroup.OLD_AGE_GROUP) return 15000
  if (ageGroup === AgeGroup.YOUNG_AGE_GROUP) return 60000
  return 20000
}

export const useDacSkipButton = ({
  isDacPage,
  isDacShowOnCtaClick,
  isPremium,
  isPremiumOrOldAge,
  isYoungOrMiddleAgeGroup,
  ageGroup,
  dacWidgetState,
  fallbackButtonText,
  getTimeout = defaultGetTimeout,
  isDacSkipButtonTriggersIgnored,
  forcedDacOnTabActionTrigger,
  experimentFlags: { isInSkipDacV4Experiment = false } = {}
}: DacSkipButtonConfig): DacSkipButtonResult => {
  const [showDacSkipButton, setShowDacSkipButton] = useState(false)
  const [skipButtonText, setSkipButtonText] = useState('')
  const [skipButtonShownReason, setSkipButtonShownReason] = useState('')

  const showDacSkipButtonRef = useRef(showDacSkipButton)

  useEffect(() => {
    showDacSkipButtonRef.current = showDacSkipButton
  }, [showDacSkipButton])

  // Show skip button when DAC CTA is clicked
  useEffect(() => {
    if (!isDacShowOnCtaClick) return
    setShowDacSkipButton(true)
    setSkipButtonShownReason('dac_widget_loaded_with_show_on_cta_click')
  }, [isDacShowOnCtaClick])

  // Determine skip button text
  useEffect(() => {
    if (isPremiumOrOldAge) {
      setSkipButtonText('Weiter ohne digitalen Kontoauszug')
    } else if (fallbackButtonText) {
      setSkipButtonText(fallbackButtonText)
    } else {
      setSkipButtonText('Ich habe keinen Online-Banking-Zugang')
    }
  }, [
    isPremiumOrOldAge,
    isInSkipDacV4Experiment,
    isYoungOrMiddleAgeGroup,
    fallbackButtonText,
    ageGroup
  ])

  // Timer-based skip button reveal
  useEffect(() => {
    if (!isDacPage || isDacSkipButtonTriggersIgnored) return

    const timeout = getTimeout({ ageGroup, isPremium })

    const timeoutId = window.setTimeout(() => {
      if (showDacSkipButtonRef.current) return

      setSkipButtonShownReason('timer')
      setShowDacSkipButton(true)
    }, timeout)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [isDacPage, isPremiumOrOldAge, ageGroup, isPremium, getTimeout])

  // Show skip button when user leaves the tab
  useEffect(() => {
    if (
      !forcedDacOnTabActionTrigger &&
      (isDacSkipButtonTriggersIgnored ||
        isPremiumOrOldAge ||
        !isDacPage ||
        !isYoungOrMiddleAgeGroup)
    ) {
      return
    }

    const onTabVisibilityChange = () => {
      if (document.visibilityState !== 'hidden') return

      setSkipButtonShownReason('left_tab')
      setShowDacSkipButton(true)
      setSkipButtonText('Weiter ohne digitalen Kontoauszug')
    }

    document.addEventListener('visibilitychange', onTabVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', onTabVisibilityChange)
    }
  }, [isPremiumOrOldAge, isDacPage, isYoungOrMiddleAgeGroup])

  // Show skip button on DAC widget error
  useEffect(() => {
    if (
      dacWidgetState === 'error' &&
      (!isPremiumOrOldAge ||
        (isInSkipDacV4Experiment &&
          !isPremiumOrOldAge &&
          ageGroup === AgeGroup.MIDDLE_AGE_GROUP))
    ) {
      setShowDacSkipButton(true)
      setSkipButtonShownReason('error_in_dac')
      setSkipButtonText('Weiter ohne digitalen Kontoauszug')
    }
  }, [dacWidgetState, isPremiumOrOldAge, isInSkipDacV4Experiment, ageGroup])

  // track events to optimizely
  useEffect(() => {
    if (skipButtonText && skipButtonShownReason) {
      broadcastToParent({
        eventName: RegisteredEventName.tracking,
        data: {
          event: 'skip_button_shown',
          context: {
            Optimizely: { userId: getCookie(COOKIE_KEY_FCID) }
          },
          payload: {
            shown_reason: skipButtonShownReason,
            text_on_button: skipButtonText
          }
        }
      })
    }
  }, [skipButtonText, skipButtonShownReason])

  return { showDacSkipButton, skipButtonText, skipButtonShownReason }
}
